// UltraFlex Admin Portal - Access Control & Logs
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  DoorOpen, CheckCircle, XCircle, AlertTriangle, Clock,
  ChevronLeft, ChevronRight, Filter, Download, Search,
  User, MapPin, Calendar, Shield
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAccess = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('logs');
  const [logs, setLogs] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [stats, setStats] = useState(null);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [siteFilter, setSiteFilter] = useState('');
  const [decisionFilter, setDecisionFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [showOverrideForm, setShowOverrideForm] = useState(false);
  const [overrideData, setOverrideData] = useState({ member_email: '', site_id: '', reason: '', duration_hours: '24' });
  const [qrInput, setQrInput] = useState('');
  const [qrResult, setQrResult] = useState(null);

  useEffect(() => {
    fetchSites();
  }, []);

  useEffect(() => {
    fetchData();
  }, [activeTab, page, siteFilter, decisionFilter, dateFilter]);

  const fetchSites = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sites`);
      const data = await res.json();
      setSites(data.items || []);
    } catch (error) {
      console.error('Failed to fetch sites:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'logs') {
        const params = new URLSearchParams({ page: page.toString(), page_size: '30' });
        if (siteFilter) params.append('site_id', siteFilter);
        if (decisionFilter) params.append('decision', decisionFilter);
        if (dateFilter) params.append('date', dateFilter);
        
        const res = await fetch(`${API_URL}/api/access/logs?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        setLogs(data.items || []);
        setTotalPages(data.total_pages || 1);
      } else if (activeTab === 'overrides') {
        const params = new URLSearchParams({ page: page.toString(), page_size: '20' });
        if (siteFilter) params.append('site_id', siteFilter);
        
        const res = await fetch(`${API_URL}/api/access/override?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        setOverrides(data.items || []);
        setTotalPages(data.total_pages || 1);
      }

      const statsParams = siteFilter ? `?site_id=${siteFilter}` : '';
      const statsRes = await fetch(`${API_URL}/api/access/stats${statsParams}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const statsData = await statsRes.json();
      setStats(statsData);
    } catch (error) {
      console.error('Failed to fetch access data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleManualOverride = async () => {
    if (!overrideData.member_email || !overrideData.reason) { alert('Please fill in member email and reason'); return; }
    try {
      const res = await fetch(`${API_URL}/api/access/override`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(overrideData)
      });
      if (res.ok) { alert('Override granted successfully'); setShowOverrideForm(false); setOverrideData({ member_email: '', site_id: '', reason: '', duration_hours: '24' }); fetchData(); }
      else { const err = await res.json().catch(() => ({})); alert(err.detail || 'Failed to create override'); }
    } catch { alert('Failed to create override'); }
  };

  const handleQrScan = async () => {
    if (!qrInput.trim()) return;
    setQrResult(null);
    try {
      const res = await fetch(`${API_URL}/api/access/scan`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ qr_data: qrInput, site_id: siteFilter || sites[0]?.id || '' })
      });
      const data = await res.json();
      setQrResult(data);
    } catch { setQrResult({ decision: 'error', message: 'Failed to process QR scan' }); }
  };

  const getDecisionBadge = (decision, isGrace) => {
    if (decision === 'allowed' && isGrace) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-500/20 text-orange-400">
          <AlertTriangle className="w-3 h-3 mr-1" />
          Grace
        </span>
      );
    }
    
    const config = {
      allowed: { color: 'green', icon: CheckCircle, label: 'Allowed' },
      denied: { color: 'red', icon: XCircle, label: 'Denied' },
      grace: { color: 'orange', icon: AlertTriangle, label: 'Grace' }
    };
    
    const c = config[decision] || { color: 'gray', icon: Clock, label: decision };
    const Icon = c.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${c.color}-500/20 text-${c.color}-400`}>
        <Icon className="w-3 h-3 mr-1" />
        {c.label}
      </span>
    );
  };

  const getDenialReasonLabel = (reason) => {
    const labels = {
      subscription_inactive: 'Inactive Subscription',
      subscription_suspended: 'Suspended',
      subscription_cancelled: 'Cancelled',
      payment_failed: 'Payment Failed',
      site_not_permitted: 'Site Not Permitted',
      token_expired: 'Token Expired',
      token_invalid: 'Invalid Token',
      token_already_used: 'Token Reused',
      member_terminated: 'Terminated',
      manual_block: 'Manual Block'
    };
    return labels[reason] || reason;
  };

  return (
    <div className="min-h-screen bg-uf-black">
      {/* Header */}
      <div className="bg-uf-surface border-b border-uf-border px-4 md:px-8 py-4 md:py-6">
        <h1 className="text-2xl font-bold text-uf-text">Access Control</h1>
        <p className="text-uf-text-muted mt-1">Monitor entry attempts and manage access overrides</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="px-4 md:px-8 py-4 md:py-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard 
            title="Total Attempts" 
            value={stats.total_attempts?.toLocaleString() || 0}
            icon={DoorOpen}
            color="blue"
          />
          <StatCard 
            title="Allowed" 
            value={stats.allowed?.toLocaleString() || 0}
            icon={CheckCircle}
            color="green"
          />
          <StatCard 
            title="Denied" 
            value={stats.denied?.toLocaleString() || 0}
            icon={XCircle}
            color="red"
          />
          <StatCard 
            title="Denial Rate" 
            value={`${((stats.denial_rate || 0) * 100).toFixed(1)}%`}
            icon={AlertTriangle}
            color="orange"
          />
        </div>
      )}

      {/* Denial Reasons Breakdown */}
      {stats?.denial_reasons?.length > 0 && (
        <div className="px-8 pb-6">
          <div className="bg-uf-surface rounded-lg border border-uf-border p-4">
            <h3 className="text-sm font-semibold text-uf-text-muted mb-3">Denial Reasons Breakdown</h3>
            <div className="flex flex-wrap gap-2">
              {stats.denial_reasons.map((r, i) => (
                <span key={i} className="px-3 py-1 bg-red-500/10 text-red-400 rounded-full text-sm">
                  {getDenialReasonLabel(r.reason)}: {r.count}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="px-4 md:px-8 border-b border-uf-border">
        <div className="flex gap-6 overflow-x-auto">
          {[
            { id: 'logs', label: 'Access Logs' },
            { id: 'stats', label: 'Statistics' },
            { id: 'overrides', label: 'Overrides' },
            { id: 'qr', label: 'QR Scan' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setPage(1); }}
              className={`py-3 border-b-2 transition-colors whitespace-nowrap text-sm ${
                activeTab === tab.id 
                  ? 'border-uf-red text-uf-red' 
                  : 'border-transparent text-uf-text-muted hover:text-uf-text'
              }`}
              data-testid={`access-tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="px-4 md:px-8 py-3 bg-uf-surface-hi border-b border-uf-border flex flex-wrap items-center gap-3">
        <Filter className="w-5 h-5 text-uf-text-muted" />
        
        <select
          value={siteFilter}
          onChange={(e) => setSiteFilter(e.target.value)}
          className="px-4 py-2 bg-uf-surface border border-uf-border rounded-lg text-uf-text"
        >
          <option value="">All Sites</option>
          {sites.map(site => (
            <option key={site.id} value={site.id}>{site.name}</option>
          ))}
        </select>

        {activeTab === 'logs' && (
          <>
            <select
              value={decisionFilter}
              onChange={(e) => setDecisionFilter(e.target.value)}
              className="px-4 py-2 bg-uf-surface border border-uf-border rounded-lg text-uf-text"
              data-testid="access-decision-filter"
            >
              <option value="">All Decisions</option>
              <option value="allowed">Allowed</option>
              <option value="denied">Denied</option>
              <option value="grace">Grace</option>
            </select>
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-4 py-2 bg-uf-surface border border-uf-border rounded-lg text-uf-text"
              data-testid="access-date-filter"
            />
            {dateFilter && (
              <button onClick={() => setDateFilter('')} className="text-xs text-uf-red hover:underline">Clear date</button>
            )}
          </>
        )}

        {activeTab === 'overrides' && (
          <button onClick={() => setShowOverrideForm(true)} className="ml-auto flex items-center px-4 py-2 bg-uf-red text-white rounded-lg text-sm hover:bg-uf-red-hover" data-testid="add-override-btn">
            <Shield className="w-4 h-4 mr-2" />Manual Override
          </button>
        )}

        {activeTab === 'logs' && (
          <button className="ml-auto flex items-center px-4 py-2 bg-uf-surface border border-uf-border rounded-lg text-uf-text hover:bg-uf-surface-hi">
            <Download className="w-4 h-4 mr-2" />Export
          </button>
        )}
      </div>

      {/* Override Modal */}
      {showOverrideForm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-uf-surface rounded-xl border border-uf-border w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-uf-text mb-4">Manual Access Override</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-uf-text-muted block mb-1">Member Email *</label>
                <input type="email" value={overrideData.member_email} onChange={e => setOverrideData(p => ({ ...p, member_email: e.target.value }))}
                  className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" data-testid="override-member-email" />
              </div>
              <div>
                <label className="text-xs text-uf-text-muted block mb-1">Site</label>
                <select value={overrideData.site_id} onChange={e => setOverrideData(p => ({ ...p, site_id: e.target.value }))}
                  className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm">
                  <option value="">All Sites</option>
                  {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-uf-text-muted block mb-1">Duration (hours)</label>
                <input type="number" value={overrideData.duration_hours} onChange={e => setOverrideData(p => ({ ...p, duration_hours: e.target.value }))}
                  className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" />
              </div>
              <div>
                <label className="text-xs text-uf-text-muted block mb-1">Reason *</label>
                <textarea value={overrideData.reason} onChange={e => setOverrideData(p => ({ ...p, reason: e.target.value }))}
                  className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm h-20 resize-none" data-testid="override-reason" />
              </div>
              <div className="flex gap-2 pt-2">
                <button onClick={() => setShowOverrideForm(false)} className="flex-1 py-2 text-uf-text-muted text-sm">Cancel</button>
                <button onClick={handleManualOverride} className="flex-1 py-2 bg-uf-red text-white rounded-lg text-sm" data-testid="submit-override-btn">Grant Override</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="p-4 md:p-8">
        {/* Stats Tab */}
        {activeTab === 'stats' && (
          <div className="space-y-6" data-testid="access-stats-tab">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard title="Total Attempts" value={stats?.total_attempts?.toLocaleString() || '0'} icon={DoorOpen} color="blue" />
              <StatCard title="Allowed" value={stats?.allowed?.toLocaleString() || '0'} icon={CheckCircle} color="green" />
              <StatCard title="Denied" value={stats?.denied?.toLocaleString() || '0'} icon={XCircle} color="red" />
              <StatCard title="Denial Rate" value={`${((stats?.denial_rate || 0) * 100).toFixed(1)}%`} icon={AlertTriangle} color="orange" />
            </div>
            {stats?.denial_reasons?.length > 0 && (
              <div className="bg-uf-surface rounded-lg border border-uf-border p-5">
                <h3 className="text-sm font-semibold text-uf-text mb-3">Denial Reasons Breakdown</h3>
                <div className="space-y-2">
                  {stats.denial_reasons.map((r, i) => (
                    <div key={i} className="flex items-center justify-between bg-uf-surface-hi rounded-lg p-3">
                      <span className="text-uf-text text-sm">{getDenialReasonLabel(r.reason)}</span>
                      <div className="flex items-center gap-3">
                        <div className="w-32 h-2 bg-uf-surface rounded-full overflow-hidden">
                          <div className="h-full bg-red-500 rounded-full" style={{ width: `${Math.min(100, (r.count / (stats.denied || 1)) * 100)}%` }} />
                        </div>
                        <span className="text-red-400 font-medium text-sm w-8 text-right">{r.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {stats?.hourly_breakdown && (
              <div className="bg-uf-surface rounded-lg border border-uf-border p-5">
                <h3 className="text-sm font-semibold text-uf-text mb-3">Peak Hours</h3>
                <p className="text-uf-text-muted text-sm">Busiest times based on access data</p>
              </div>
            )}
          </div>
        )}

        {/* QR Scan Tab */}
        {activeTab === 'qr' && (
          <div className="max-w-lg mx-auto" data-testid="access-qr-scan-tab">
            <div className="bg-uf-surface rounded-xl border border-uf-border p-6 text-center">
              <DoorOpen className="w-12 h-12 text-uf-red mx-auto mb-4" />
              <h3 className="text-lg font-bold text-uf-text mb-2">QR Code Scanner</h3>
              <p className="text-uf-text-muted text-sm mb-6">Enter or scan a member's QR code to validate access</p>
              <div className="flex gap-2 mb-4">
                <input type="text" value={qrInput} onChange={e => setQrInput(e.target.value)} placeholder="Paste or scan QR token..."
                  className="flex-1 bg-uf-surface-hi border border-uf-border rounded-lg px-4 py-3 text-uf-text text-sm" data-testid="qr-scan-input"
                  onKeyDown={e => e.key === 'Enter' && handleQrScan()} />
                <button onClick={handleQrScan} className="px-6 py-3 bg-uf-red text-white rounded-lg font-semibold text-sm" data-testid="qr-scan-btn">Scan</button>
              </div>
              {qrResult && (
                <div className={`rounded-xl p-4 mt-4 ${qrResult.decision === 'allowed' ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'}`} data-testid="qr-scan-result">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    {qrResult.decision === 'allowed' ? <CheckCircle className="w-6 h-6 text-emerald-400" /> : <XCircle className="w-6 h-6 text-red-400" />}
                    <span className={`text-lg font-bold ${qrResult.decision === 'allowed' ? 'text-emerald-400' : 'text-red-400'}`}>
                      {qrResult.decision === 'allowed' ? 'ACCESS GRANTED' : 'ACCESS DENIED'}
                    </span>
                  </div>
                  {qrResult.member_name && <p className="text-uf-text text-sm">{qrResult.member_name}</p>}
                  {qrResult.reason && <p className="text-uf-text-muted text-xs mt-1">{getDenialReasonLabel(qrResult.reason)}</p>}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Logs & Overrides Tabs */}
        {(activeTab === 'logs' || activeTab === 'overrides') && (
        <div className="bg-uf-surface rounded-lg border border-uf-border overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-uf-red mx-auto"></div>
            </div>
          ) : activeTab === 'logs' ? (
            <AccessLogsTable 
              logs={logs} 
              getDecisionBadge={getDecisionBadge}
              getDenialReasonLabel={getDenialReasonLabel}
            />
          ) : (
            <OverridesTable overrides={overrides} />
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 bg-uf-surface-hi border-t border-uf-border flex items-center justify-between">
              <span className="text-uf-text-muted text-sm">Page {page} of {totalPages}</span>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 rounded bg-uf-surface border border-uf-border text-uf-text disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-2 rounded bg-uf-surface border border-uf-border text-uf-text disabled:opacity-50"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon: Icon, color }) => (
  <div className="bg-uf-surface rounded-lg p-5 border border-uf-border">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-uf-text-muted text-sm">{title}</p>
        <p className="text-2xl font-bold text-uf-text mt-1">{value}</p>
      </div>
      <div className={`p-3 rounded-lg bg-${color}-500/10`}>
        <Icon className={`w-6 h-6 text-${color}-500`} />
      </div>
    </div>
  </div>
);

const AccessLogsTable = ({ logs, getDecisionBadge, getDenialReasonLabel }) => (
  <table className="w-full">
    <thead>
      <tr className="bg-uf-surface-hi">
        <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Time</th>
        <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Member</th>
        <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Site</th>
        <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Decision</th>
        <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Reason</th>
      </tr>
    </thead>
    <tbody className="divide-y divide-uf-border">
      {logs.length === 0 ? (
        <tr>
          <td colSpan="5" className="px-6 py-12 text-center text-uf-text-muted">
            <DoorOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
            No access attempts recorded
          </td>
        </tr>
      ) : (
        logs.map((log) => (
          <tr key={log.id} className="hover:bg-uf-surface-hi">
            <td className="px-6 py-4 text-uf-text-muted text-sm">
              {new Date(log.timestamp).toLocaleString()}
            </td>
            <td className="px-6 py-4">
              <div className="flex items-center">
                <User className="w-4 h-4 text-uf-text-muted mr-2" />
                <div>
                  <p className="text-uf-text">{log.member_name || 'Unknown'}</p>
                  <p className="text-uf-text-muted text-xs font-mono">{log.member_number || log.member_id?.slice(0, 8)}</p>
                </div>
              </div>
            </td>
            <td className="px-6 py-4">
              <div className="flex items-center">
                <MapPin className="w-4 h-4 text-uf-text-muted mr-2" />
                <span className="text-uf-text">{log.site_id}</span>
              </div>
            </td>
            <td className="px-6 py-4">
              {getDecisionBadge(log.decision, log.is_grace)}
            </td>
            <td className="px-6 py-4 text-uf-text-muted text-sm">
              {log.reason ? getDenialReasonLabel(log.reason) : '-'}
            </td>
          </tr>
        ))
      )}
    </tbody>
  </table>
);

const OverridesTable = ({ overrides }) => (
  <table className="w-full">
    <thead>
      <tr className="bg-uf-surface-hi">
        <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Member</th>
        <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Site</th>
        <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Valid Until</th>
        <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Reason</th>
        <th className="px-6 py-3 text-right text-xs font-semibold text-uf-text-muted uppercase">Actions</th>
      </tr>
    </thead>
    <tbody className="divide-y divide-uf-border">
      {(!overrides || overrides.length === 0) ? (
        <tr>
          <td colSpan="5" className="px-6 py-12 text-center text-uf-text-muted">
            <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
            No active overrides
          </td>
        </tr>
      ) : (
        overrides.map((override) => (
          <tr key={override.id} className="hover:bg-uf-surface-hi">
            <td className="px-6 py-4 text-uf-text">{override.member_name}</td>
            <td className="px-6 py-4 text-uf-text">{override.site_id || 'All Sites'}</td>
            <td className="px-6 py-4 text-uf-text-muted">
              {new Date(override.valid_until).toLocaleString()}
            </td>
            <td className="px-6 py-4 text-uf-text-muted">{override.reason}</td>
            <td className="px-6 py-4 text-right">
              <button className="text-red-400 hover:text-red-300 text-sm">
                Revoke
              </button>
            </td>
          </tr>
        ))
      )}
    </tbody>
  </table>
);

export default AdminAccess;
